import type { RestConnectorConfig } from "oi-ecp-data-connector";
import { Public } from "oi-ecp-data-connector";

import { DataConnectorBase } from "./dataConnectorBase";
import type {
  TemplateCategory,
  TemplatesResponse,
  CreateDraftOperationResponse,
  PublishTemplateOperationResponse,
  CloneTemplateOperationResponse,
  ActivateTemplateVersionResponse,
  RenameTemplateOperationResponse,
  DeleteTemplateOperationResponse,
  GetTemplatePresignedUrlResponse,
  ImportTemplateOperationResponse,
} from "../redux/templates/templatesTypes";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

if (!API_BASE_URL || API_BASE_URL.trim().length === 0) {
  throw new Error("Missing required environment variable: VITE_API_BASE_URL");
}

// NOTE: These endpoints are assumed to exist in your backend and were previously
// called via RestDataConnector.restCall("<name>", ...).
// If any path differs, update the url/method for that endpoint here.
const config: RestConnectorConfig = {
  baseUrl: API_BASE_URL,
  defaultHeaders: {
    "Content-Type": "application/json",
  },
  restApiConfig: {
    getTemplates: {
      url: "/?platformId={platformId}&clientId={clientId}&templateCategory={templateCategory}",
      method: "GET",
    },
    renameTemplate: {
      url: "/rename",
      method: "PUT",
    },
    publishTemplate: {
      url: "/publish",
      method: "POST",
    },
    cloneTemplate: {
      url: "/clone",
      method: "POST",
    },
    createDraft: {
      url: "/draft",
      method: "POST",
    },
    activateTemplateVersion: {
      url: "/activate?templateId={templateRowId}&templateVersionId={templateVersionRowId}",
      method: "PUT",
    },
    deleteTemplate: {
      url: "/?templateId={templateId}",
      method: "DELETE",
    },
    getTemplatePresignedUrl: {
      url: "/presigned?s3Path={s3Path}",
      method: "GET",
    },
    importTemplate: {
      url: "/import",
      method: "POST",
    },
  },
};

export class TemplatesDataConnector extends DataConnectorBase {
  private static templateHtmlCache = new Map<string, string>();

  private static templateFetchCache = new Map<string, Promise<string>>();

  constructor() {
    super(config);
  }

  private validateClientId(clientId: string) {
    if (clientId.trim().length === 0) {
      throw new Error("Invalid clientId");
    }
  }

  private validateToken(token: string) {
    if (token.trim().length === 0) {
      throw new Error("Invalid token");
    }
  }

  private validateTemplateName(templateName: string) {
    if (templateName.trim().length === 0) {
      throw new Error("Invalid templateName");
    }
  }

  private setAuth(token?: string) {
    // apply token to each endpoint used here
    this.setEndpointAuthHeader("getTemplates", token);
    this.setEndpointAuthHeader("renameTemplate", token);
    this.setEndpointAuthHeader("createDraft", token);
    this.setEndpointAuthHeader("publishTemplate", token);
    this.setEndpointAuthHeader("cloneTemplate", token);
    this.setEndpointAuthHeader("activateTemplateVersion", token);
    this.setEndpointAuthHeader("deleteTemplate", token);
    this.setEndpointAuthHeader("getTemplatePresignedUrl", token);
    this.setEndpointAuthHeader("importTemplate", token);
  }

  @Public({
    description: "Get templates for a client.",
  })
  async getTemplates(
    clientId: string | undefined,
    platformId: string | undefined,
    templateCategory: TemplateCategory | undefined,
    token: string
  ): Promise<TemplatesResponse> {
    if (clientId) this.validateClientId(clientId);
    this.validateToken(token);
    this.setAuth(token);
    return this.restCallStrict<TemplatesResponse>("getTemplates", null, {
      platformId: platformId ?? "",
      clientId: clientId ?? "",
      templateCategory: templateCategory ?? "",
    });
  }

  @Public({ description: "Rename a template." })
  async renameTemplate(
    body: {
      templateId: string;
      templateName: string;
      templateFolder?: string;
      templateCategory: TemplateCategory;
      platformId?: string;
      platformClientsId?: string;
    },
    token: string,
  ): Promise<RenameTemplateOperationResponse> {
    this.validateToken(token);
    this.validateTemplateName(body.templateName);
    this.setAuth(token);
    return this.restCallStrict<RenameTemplateOperationResponse>(
      "renameTemplate",
      body,
    );
  }

  @Public({ description: "Publish a template." })
  async publishTemplate(
    body: {
      platformClientsId: string;
      platformId: string;
      templateId: string;
    },
    token: string,
  ): Promise<PublishTemplateOperationResponse> {
    this.validateToken(token);
    this.setAuth(token);
    return this.restCallStrict<PublishTemplateOperationResponse>(
      "publishTemplate",
      body,
    );
  }

  @Public({ description: "Clone a template." })
  async cloneTemplate(
    body: {
      templateName: string;
      templateFolder?: string;
      templateCategory: string;
      platformClientsId?: string;
      platformId?: string;
      templateVersionId: string;
      templateId: string;
    },
    token: string,
  ): Promise<CloneTemplateOperationResponse> {
    this.validateToken(token);
    this.validateTemplateName(body.templateName);
    this.setAuth(token);
    return this.restCallStrict<CloneTemplateOperationResponse>(
      "cloneTemplate",
      body,
    );
  }

  @Public({ description: "Create a draft template." })
  async createDraft(
    body: {
      templateName: string;
      templateFolder: string;
      templateCategory: TemplateCategory;
      platformClientsId?: string;
      platformId?: string;
      templateVersionId: string;
      templateId: string;
    },
    token: string,
  ): Promise<CreateDraftOperationResponse> {
    this.validateToken(token);
    this.setAuth(token);
    return this.restCallStrict<CreateDraftOperationResponse>(
      "createDraft",
      body,
    );
  }

  @Public({ description: "Activate a specific published version of a template." })
  async activateTemplateVersion(
    templateRowId: string,
    templateVersionRowId: string,
    token: string,
    action: "activate" | "deactivate",
  ): Promise<ActivateTemplateVersionResponse> {
    this.validateToken(token);
    if (!templateRowId || !/^\d+$/.test(templateRowId)) {
      throw new Error("templateRowId must contain only numeric characters");
    }
    if (!templateVersionRowId || !/^\d+$/.test(templateVersionRowId)) {
      throw new Error("templateVersionRowId must contain only numeric characters");
    }
    this.setAuth(token);
    return this.restCallStrict<ActivateTemplateVersionResponse>(
      "activateTemplateVersion",
      { action },
      { templateRowId, templateVersionRowId },
    );
  }

  @Public({ description: "Delete a template." })
  async deleteTemplate(
    body: {
      templateRowId: string;
      templateCategory: TemplateCategory;
      platformId?: string;
      platformClientsId?: string;
    },
    token: string,
  ): Promise<DeleteTemplateOperationResponse> {
    this.validateToken(token);
    if (body.templateRowId.trim().length === 0) {
      throw new Error("Invalid templateRowId");
    }
    this.setAuth(token);
    return this.restCallStrict<DeleteTemplateOperationResponse>(
      "deleteTemplate",
      null,
      { templateId: body.templateRowId },
    );
  }

  @Public({ description: "Get a short-lived presigned GET URL for an S3 template object." })
  async getTemplatePresignedUrl(
    s3Path: string,
    token: string,
  ): Promise<GetTemplatePresignedUrlResponse> {
    if (s3Path.trim().length === 0) {
      throw new Error("Invalid s3Path");
    }
    this.validateToken(token);
    this.setAuth(token);
    return this.restCallStrict<GetTemplatePresignedUrlResponse>(
      "getTemplatePresignedUrl",
      null,
      { s3Path },
    );
  }

  @Public({ description: "Import a template using metadata tags from exported HTML." })
  async importTemplate(
    body: {
      templateId: string;
      templateName: string;
      templateFolder?: string;
      templateCategory: "OPTUM" | "PLATFORM" | "CLIENT";
      account?: string;
      client?: string;
    },
    token: string,
  ): Promise<ImportTemplateOperationResponse> {
    this.validateToken(token);
    this.setAuth(token);
    return this.restCallStrict<ImportTemplateOperationResponse>(
      "importTemplate",
      body,
    );
  }

  @Public({ description: "Fetch template preview HTML by URL." })
  async fetchTemplatePreviewHtml(cacheKey: string, src: string): Promise<string> {
    if (cacheKey.trim().length === 0) {
      throw new Error("Invalid cacheKey");
    }
    if (src.trim().length === 0) {
      throw new Error("Invalid src");
    }

    if (TemplatesDataConnector.templateHtmlCache.has(cacheKey)) {
      return TemplatesDataConnector.templateHtmlCache.get(cacheKey)!;
    }

    if (TemplatesDataConnector.templateFetchCache.has(cacheKey)) {
      return TemplatesDataConnector.templateFetchCache.get(cacheKey)!;
    }

    const promise = fetch(src)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch template: ${response.status}`);
        }
        return response.text();
      })
      .then((html) => {
        TemplatesDataConnector.templateHtmlCache.set(cacheKey, html);
        TemplatesDataConnector.templateFetchCache.delete(cacheKey);
        return html;
      })
      .catch((error) => {
        TemplatesDataConnector.templateFetchCache.delete(cacheKey);
        throw error;
      });

    TemplatesDataConnector.templateFetchCache.set(cacheKey, promise);
    return promise;
  }
}